/**
 * Servicio para operaciones administrativas
 * GestiÃ³n de clientes, servicios, pagos y auditorÃ­a
 */
import { ApiService } from './api';

// Instancia separada que apunta al API Gateway del admin dashboard
// El token JWT se configura desde AdminAuthContext vía setTokenGetter()
const ADMIN_API_URL = process.env.REACT_APP_ADMIN_API_URL || process.env.REACT_APP_API_BASE_URL || 'http://localhost:3000/api';
export const adminApiService = new ApiService(ADMIN_API_URL);

// Tipos e interfaces
export interface Client {
  tenantId: string;
  tenantName: string;
  email: string;
  status: 'ACTIVE' | 'SUSPENDED' | 'INACTIVE';
  createdAt: string | Date;
  updatedAt: string | Date;
  totalCost?: number;
  deuda?: number;
}

export interface ClientDetailResponse {
  tenant: Client;
  services: Service[];
  totalCost: number;
  deuda: number;
  success?: boolean;
  error?: string;
}

export interface ClientListResponse {
  clients: Client[];
  total: number;
  success?: boolean;
  error?: string;
}

export interface Service {
  serviceId: string;
  tenantId: string;
  name: string;
  type: string;
  status: 'RUNNING' | 'STOPPED' | 'TERMINATED' | 'ERROR' | 'CREATING' | 'FAILED' | 'DELETING' | 'DELETED';
  createdAt: string | Date;
  updatedAt: string | Date;
  cost?: number;
  endpointUrl?: string;
  region?: string;
  ec2InstanceId?: string;
  dbInstanceId?: string;
  instanceType?: string;
  stackName?: string;
  volumeGb?: number;
  hasMetrics?: boolean;
}

export interface MetricSeries {
  label: string;
  unit: string;
  timestamps: string[];
  values: number[];
  current: number | null;
  max: number | null;
  min: number | null;
  error?: string;
}

export interface ServiceMetrics {
  service_id: string;
  service_type: string;
  instance_id?: string;
  has_metrics: boolean;
  message?: string;
  range_hours?: number;
  period_seconds?: number;
  metrics: Record<string, MetricSeries>;
}

export interface ServiceDetailResponse {
  service: Service;
  apps: App[];
  logs: LogEntry[];
  cost: number;
  totalCostThisMonth?: number;
  events?: any[];
  /** Costo AWS real del mes en curso (Cost Explorer con fallback estimado) */
  awsCost?: {
    month_to_date: number;
    projected_month_end: number;
    /** Acumulado desde la creación del servicio (null si CE no devolvió dato) */
    since_creation?: number | null;
    /** Fecha de inicio real del acumulado (recortada a la retención de CE) */
    since_creation_start?: string | null;
    /** true si la creación es anterior a la ventana de retención de CE (~14m) */
    since_creation_truncated?: boolean;
    currency: string;
    source: 'cost_explorer' | 'estimated';
    scope: 'service' | 'tenant';
    days_elapsed: number;
    days_in_month: number;
  } | null;
  /** Plan contratado por el cliente para este servicio */
  plan?: {
    planCode: string;
    variantCode?: string;
    variantName?: string;
    planName?: string;
    typeFamily?: string;
    priceAmount?: number;
    currency?: string;
    prices?: Record<string, number>;
    billingInterval?: string;
    addons?: string[];
    addonDetails?: { addonCode: string; addonName: string; priceAmount: number; currency: string }[];
    orderId?: string;
  } | null;
  success?: boolean;
  error?: string;
}

export interface App {
  appId: string;
  serviceId: string;
  name: string;
  runtime: string;
  status: string;
}

export interface LogEntry {
  timestamp: string | Date;
  level: 'INFO' | 'ERROR' | 'WARN' | 'DEBUG';
  message: string;
}

export interface Payment {
  paymentId: string;
  tenantId: string;
  amount: number;
  currency: string;
  status: 'PENDING' | 'PAID' | 'FAILED' | 'OVERDUE';
  dueDate: string | Date;
  paidDate?: string | Date;
  invoice?: string;
}

export interface PaymentListResponse {
  payments: Payment[];
  total: number;
  success?: boolean;
  error?: string;
}

export interface AuditLog {
  auditId: string;
  action: string;
  actor: string;
  tenantId?: string;
  resourceId?: string;
  details?: Record<string, any>;
  timestamp: string | Date;
  status: 'SUCCESS' | 'FAILURE';
}

export interface AuditLogListResponse {
  logs: AuditLog[];
  total: number;
  success?: boolean;
  error?: string;
}

export interface CostBreakdownItem {
  service_id: string;
  aws_service: string;
  cost: number;
}

export interface TenantCostResponse {
  tenant_id: string;
  period_days: number;
  date_start: string;
  date_end: string;
  total_cost: number;
  currency: string;
  source: 'cost_explorer' | 'estimated';
  breakdown: CostBreakdownItem[];
  daily?: Array<{ date: string; cost: number }>;
  cached_at?: string;
  cache_expires_at?: string;
}

export interface AdminActionResponse {
  success: boolean;
  auditId?: string;
  message?: string;
  error?: string;
  errorCode?: string;
}

export interface MarkPaymentPaidResponse {
  success: boolean;
  newBalance: number;
  auditId?: string;
}

/**
 * AdminService: GestiÃ³n centralizada de operaciones administrativas
 */
// -----------------------------------------------------------------------------
// Configuración global de visibilidad de la UI del cliente
// -----------------------------------------------------------------------------

/**
 * Estructura de secciones. Cada sección es un mapa de banderas booleanas,
 * anidado como máximo 2 niveles. El backend devuelve el documento completo
 * fusionado con sus DEFAULTS, así que la UI siempre tiene todas las banderas.
 */
export interface UISettings {
  menu?: { items?: Record<string, boolean> };
  marketplace?: { enabled?: boolean; products?: Record<string, boolean> };
  appDeploy?: {
    enabled?: boolean;
    static?: boolean;
    server?: boolean;
    staticFrameworks?: Record<string, boolean>;
    serverRuntimes?: Record<string, boolean>;
  };
  database?: { enabled?: boolean; engines?: Record<string, boolean> };
  vm?: { enabled?: boolean; products?: Record<string, boolean> };
}

export interface SettingsResponse {
  settings: UISettings | null;
  version?: number;
  updatedAt?: string | null;
  updatedBy?: string | null;
  source?: string;
  success?: boolean;
  error?: string;
}

export class AdminService {
  private baseEndpoint = '/admin';

  /**
   * Obtiene la lista de clientes con paginaciÃ³n y filtros
   */
  async getClients(params?: {
    limit?: number;
    offset?: number;
    status?: 'ACTIVE' | 'SUSPENDED' | 'INACTIVE';
  }): Promise<ClientListResponse> {
    const queryParams = new URLSearchParams();

    if (params?.limit) queryParams.append('limit', params.limit.toString());
    if (params?.offset) queryParams.append('offset', params.offset.toString());
    if (params?.status) queryParams.append('status', params.status);

    const queryString = queryParams.toString();
    const endpoint = queryString ? `${this.baseEndpoint}/clients?${queryString}` : `${this.baseEndpoint}/clients`;

    try {
      const response = await adminApiService.get<any>(endpoint);
      const normalized = this.normalizeClientListResponse(response);
      return { ...normalized, success: true };
    } catch (error) {
      return this.handleApiError(error) as ClientListResponse;
    }
  }

  /**
   * Obtiene los detalles de un cliente especÃ­fico
   */
  async getClientDetail(tenantId: string): Promise<ClientDetailResponse> {
    const endpoint = `${this.baseEndpoint}/clients/${tenantId}`;

    try {
      const response = await adminApiService.get<any>(endpoint);
      const payload = this.unwrap(response);
      const rawTenant = payload.tenant || null;
      if (!rawTenant) {
        return { tenant: null as any, services: [], totalCost: 0, deuda: 0, success: false, error: 'Client not found' };
      }
      return {
        tenant: this.normalizeTenantItem(rawTenant),
        services: (payload.services || []).map((s: any) => this.normalizeServiceItem(s)),
        totalCost: payload.total_cost_this_month ?? payload.totalCost ?? 0,
        deuda: payload.deuda ?? 0,
        success: true,
      };
    } catch (error) {
      return this.handleApiError(error) as ClientDetailResponse;
    }
  }

  /**
   * Suspende un cliente
   */
  async suspendClient(tenantId: string, reason?: string): Promise<AdminActionResponse> {
    const endpoint = `${this.baseEndpoint}/clients/${tenantId}/suspend`;

    try {
      const response = await adminApiService.post<any>(endpoint, { reason });
      return {
        success: response.success || true,
        auditId: response.auditId,
        message: response.message,
      };
    } catch (error) {
      return this.handleApiError(error);
    }
  }

  /**
   * Reactiva un cliente
   */
  async reactivateClient(tenantId: string): Promise<AdminActionResponse> {
    const endpoint = `${this.baseEndpoint}/clients/${tenantId}/reactivate`;

    try {
      const response = await adminApiService.post<any>(endpoint, {});
      return {
        success: response.success || true,
        auditId: response.auditId,
        message: response.message,
      };
    } catch (error) {
      return this.handleApiError(error);
    }
  }

  /**
   * Obtiene la lista de servicios con filtros opcionales
   */
  async getServices(params?: {
    tenantId?: string;
    status?: string;
    type?: string;
  }): Promise<{ services: Service[]; success?: boolean; error?: string }> {
    const queryParams = new URLSearchParams();

    if (params?.tenantId) queryParams.append('tenantId', params.tenantId);
    if (params?.status) queryParams.append('status', params.status);
    if (params?.type) queryParams.append('type', params.type);

    const queryString = queryParams.toString();
    const endpoint = queryString ? `${this.baseEndpoint}/services?${queryString}` : `${this.baseEndpoint}/services`;

    try {
      const response = await adminApiService.get<any>(endpoint);
      const payload = this.unwrap(response);
      const raw: any[] = Array.isArray(payload) ? payload : (payload?.services ?? []);
      const services = raw.map((item) => this.normalizeServiceItem(item));
      return { services, success: true };
    } catch (error) {
      return this.handleApiError(error);
    }
  }

  /**
   * Obtiene los detalles de un servicio específico
   */
  async getServiceDetail(serviceId: string): Promise<ServiceDetailResponse> {
    const endpoint = `${this.baseEndpoint}/services/${serviceId}`;

    try {
      const response = await adminApiService.get<any>(endpoint);
      const payload = this.unwrap(response);
      const raw = payload.service || {};
      const service: Service = {
        serviceId: raw.id || raw.serviceId || '',
        tenantId: raw.tenant_id || raw.tenantId || '',
        name: raw.name || '',
        type: raw.type || '',
        status: ((raw.status || 'stopped').toUpperCase()) as Service['status'],
        createdAt: raw.created_at || raw.createdAt || '',
        updatedAt: raw.updated_at || raw.updatedAt || '',
        cost: raw.cost_per_hour ?? 0,
        endpointUrl: raw.endpoint_url || '',
        region: raw.region || 'us-east-1',
        ec2InstanceId: raw.ec2_instance_id || '',
        dbInstanceId: raw.db_instance_id || '',
        instanceType: raw.instance_type || '',
        stackName: raw.stack_name || '',
        volumeGb: raw.volume_gb || 0,
        hasMetrics: raw.has_metrics || false,
      };
      return {
        service,
        apps: payload.apps || [],
        logs: payload.logs || [],
        cost: raw.cost_per_hour ?? 0,
        totalCostThisMonth: payload.total_cost_this_month,
        events: payload.events || [],
        awsCost: payload.aws_cost || null,
        plan: payload.plan || null,
        success: true,
      };
    } catch (error) {
      return this.handleApiError(error) as ServiceDetailResponse;
    }
  }

  /**
   * Obtiene métricas de CloudWatch para un servicio
   */
  async getServiceMetrics(
    serviceId: string,
    rangeHours: number = 1,
    periodSeconds: number = 300,
    metricsFilter?: string[],
  ): Promise<{ data: ServiceMetrics | null; success: boolean; error?: string }> {
    const qp = new URLSearchParams();
    qp.append('range', rangeHours.toString());
    qp.append('period', periodSeconds.toString());
    if (metricsFilter?.length) qp.append('metrics', metricsFilter.join(','));

    const endpoint = `${this.baseEndpoint}/services/${serviceId}/metrics?${qp}`;

    try {
      const response = await adminApiService.get<any>(endpoint);
      const payload = this.unwrap(response);
      return { data: payload, success: true };
    } catch (error) {
      const err = this.handleApiError(error);
      return { data: null, success: false, error: err.error };
    }
  }

  /**
   * Detiene un servicio
   */
  async stopService(serviceId: string, reason?: string): Promise<AdminActionResponse> {
    const endpoint = `${this.baseEndpoint}/services/${serviceId}/stop`;

    try {
      const response = await adminApiService.post<any>(endpoint, { reason });
      return {
        success: response.success || true,
        auditId: response.auditId,
        message: response.message,
      };
    } catch (error) {
      return this.handleApiError(error);
    }
  }

  /**
   * Elimina un servicio
   */
  async deleteService(serviceId: string, reason?: string): Promise<AdminActionResponse> {
    const endpoint = `${this.baseEndpoint}/services/${serviceId}`;

    try {
      const response = await adminApiService.delete<any>(endpoint);
      return {
        success: response.success || true,
        auditId: response.auditId,
        message: response.message,
      };
    } catch (error) {
      return this.handleApiError(error);
    }
  }

  /**
   * Obtiene la lista de pagos con filtros opcionales
   */
  async getPayments(params?: {
    tenantId?: string;
    status?: string;
    dateFrom?: string;
    dateTo?: string;
  }): Promise<PaymentListResponse> {
    const queryParams = new URLSearchParams();

    if (params?.tenantId) queryParams.append('tenantId', params.tenantId);
    if (params?.status) queryParams.append('status', params.status);
    if (params?.dateFrom) queryParams.append('dateFrom', params.dateFrom);
    if (params?.dateTo) queryParams.append('dateTo', params.dateTo);

    const queryString = queryParams.toString();
    const endpoint = queryString ? `${this.baseEndpoint}/payments?${queryString}` : `${this.baseEndpoint}/payments`;

    try {
      const response = await adminApiService.get<any>(endpoint);
      const normalized = this.normalizePaymentListResponse(response);
      return { ...normalized, success: true };
    } catch (error) {
      return this.handleApiError(error) as PaymentListResponse;
    }
  }

  /**
   * Marca un pago como pagado
   */
  async markPaymentPaid(paymentId: string): Promise<MarkPaymentPaidResponse> {
    const endpoint = `${this.baseEndpoint}/payments/${paymentId}/mark-paid`;

    try {
      const response = await adminApiService.post<any>(endpoint, {});
      return {
        success: response.success || true,
        newBalance: response.newBalance || 0,
        auditId: response.auditId,
      };
    } catch (error) {
      return this.handleApiError(error);
    }
  }

  /**
   * Obtiene el registro de auditorÃ­a con filtros opcionales
   */
  async getAuditLog(params?: {
    tenantId?: string;
    action?: string;
    dateFrom?: string;
    dateTo?: string;
    limit?: number;
  }): Promise<AuditLogListResponse> {
    const queryParams = new URLSearchParams();

    if (params?.tenantId) queryParams.append('tenantId', params.tenantId);
    if (params?.action) queryParams.append('action', params.action);
    if (params?.dateFrom) queryParams.append('dateFrom', params.dateFrom);
    if (params?.dateTo) queryParams.append('dateTo', params.dateTo);
    if (params?.limit) queryParams.append('limit', params.limit.toString());

    const queryString = queryParams.toString();
    const endpoint = queryString ? `${this.baseEndpoint}/audit?${queryString}` : `${this.baseEndpoint}/audit`;

    try {
      const response = await adminApiService.get<any>(endpoint);
      const normalized = this.normalizeAuditLogListResponse(response);
      return { ...normalized, success: true };
    } catch (error) {
      return this.handleApiError(error) as AuditLogListResponse;
    }
  }

  /**
   * Obtiene costos AWS reales o estimados de un tenant.
   * El endpoint cachea resultados 24h en DynamoDB (costo $0.01/consulta a Cost Explorer).
   */
  async getTenantCosts(
    tenantId: string,
    days: 7 | 30 | 90 = 30,
  ): Promise<{
    data: TenantCostResponse | null;
    success: boolean;
    error?: string;
  }> {
    const endpoint = `${this.baseEndpoint}/costs?tenantId=${encodeURIComponent(tenantId)}&days=${days}`;
    try {
      const response = await adminApiService.get<any>(endpoint);
      const payload = this.unwrap(response);
      return { data: payload, success: true };
    } catch (error) {
      const err = this.handleApiError(error);
      return { data: null, success: false, error: err.error };
    }
  }

  /**
   * Lista todos los productos del catálogo (tabla Products), incluidos inactivos.
   */
  async getProducts(params?: {
    category?: string;
    includeInactive?: boolean;
  }): Promise<ProductListResponse> {
    const queryParams = new URLSearchParams();
    if (params?.category) queryParams.append('category', params.category);
    if (params?.includeInactive === false) queryParams.append('include_inactive', 'false');

    const queryString = queryParams.toString();
    const endpoint = queryString
      ? `${this.baseEndpoint}/products?${queryString}`
      : `${this.baseEndpoint}/products`;

    try {
      const response = await adminApiService.get<any>(endpoint);
      const payload = this.unwrap(response);
      return {
        products: payload?.products ?? [],
        total: payload?.total ?? 0,
        success: true,
      };
    } catch (error) {
      return { ...this.handleApiError(error), products: [], total: 0 };
    }
  }

  /**
   * Actualiza campos de un producto. Los campos de identidad (PK, SK, id,
   * createdAt) están protegidos en el backend.
   */
  async updateProduct(
    productId: string,
    updates: Partial<Product>,
  ): Promise<ProductActionResponse> {
    const endpoint = `${this.baseEndpoint}/products/${encodeURIComponent(productId)}`;
    try {
      const response = await adminApiService.patch<any>(endpoint, updates);
      const payload = this.unwrap(response);
      return { product: payload?.product ?? null, success: true };
    } catch (error) {
      return { ...this.handleApiError(error), product: null };
    }
  }

  /**
   * Crea un producto nuevo. Requiere id, name, type, category y typeFamily.
   */
  async createProduct(product: Partial<Product>): Promise<ProductActionResponse> {
    const endpoint = `${this.baseEndpoint}/products`;
    try {
      const response = await adminApiService.post<any>(endpoint, product);
      const payload = this.unwrap(response);
      return { product: payload?.product ?? null, success: true };
    } catch (error) {
      return { ...this.handleApiError(error), product: null };
    }
  }

  /**
   * Lista el catálogo de precios completo: planes (PRICE) y add-ons
   * (ADDON_PRICE), activos e inactivos.
   */
  async getPricing(typeFamily?: string): Promise<PricingListResponse> {
    const endpoint = typeFamily
      ? `${this.baseEndpoint}/pricing?type_family=${encodeURIComponent(typeFamily)}`
      : `${this.baseEndpoint}/pricing`;
    try {
      const response = await adminApiService.get<any>(endpoint);
      const payload = this.unwrap(response);
      return {
        plans: payload?.plans ?? [],
        addons: payload?.addons ?? [],
        total: payload?.total ?? 0,
        config: payload?.config,
        success: true,
      };
    } catch (error) {
      return { ...this.handleApiError(error), plans: [], addons: [], total: 0 };
    }
  }

  /**
   * Crea un add-on nuevo. Nace INACTIVE.
   */
  async createPricingAddon(payload: PricingAddonCreate): Promise<PricingActionResponse> {
    try {
      const response = await adminApiService.post<any>(`${this.baseEndpoint}/pricing/addons`, payload);
      const data = this.unwrap(response);
      return { item: data?.item ?? null, success: true };
    } catch (error) {
      return { ...this.handleApiError(error), item: null };
    }
  }

  /**
   * Crea un plan nuevo desde cero (para familias sin planes en el catálogo).
   * Nace INACTIVE.
   */
  async createPricingPlan(payload: PricingPlanCreate): Promise<PricingActionResponse> {
    try {
      const response = await adminApiService.post<any>(`${this.baseEndpoint}/pricing`, payload);
      const data = this.unwrap(response);
      return { item: data?.item ?? null, success: true };
    } catch (error) {
      return { ...this.handleApiError(error), item: null };
    }
  }

  /**
   * Crea una variante nueva de un plan duplicando una existente.
   * Se crea INACTIVE por default para que no salga a la venta a medias.
   */
  async createPricingVariant(payload: PricingVariantCreate): Promise<PricingActionResponse> {
    try {
      const response = await adminApiService.post<any>(`${this.baseEndpoint}/pricing`, payload);
      const data = this.unwrap(response);
      return { item: data?.item ?? null, success: true };
    } catch (error) {
      return { ...this.handleApiError(error), item: null };
    }
  }

  /**
   * Actualiza el tipo de cambio de referencia USD→MXN del catálogo.
   */
  async updatePricingConfig(usdToMxn: number): Promise<{ success: boolean; error?: string }> {
    try {
      await adminApiService.patch<any>(`${this.baseEndpoint}/pricing/config`, { usdToMxn });
      return { success: true };
    } catch (error) {
      return this.handleApiError(error);
    }
  }

  /**
   * Actualiza un item de precios identificado por PK + SK. Enviar
   * expectedVersion (el `version` leído) para detectar ediciones concurrentes.
   */
  async updatePricingItem(
    update: PricingItemUpdate,
  ): Promise<PricingActionResponse> {
    const endpoint = `${this.baseEndpoint}/pricing`;
    try {
      const response = await adminApiService.patch<any>(endpoint, update);
      const payload = this.unwrap(response);
      return { item: payload?.item ?? null, success: true };
    } catch (error) {
      return { ...this.handleApiError(error), item: null };
    }
  }

  // -------------------------------------------------------------------------
  // Notificaciones / Email (SES) — Fase A: monitoreo + identidades
  // -------------------------------------------------------------------------

  /** Estado de la cuenta SES: sandbox/producción, cuota, identidades, métricas. */
  async getNotificationStatus(): Promise<NotificationStatusResponse> {
    try {
      const response = await adminApiService.get<any>(`${this.baseEndpoint}/notifications/status`);
      const payload = this.unwrap(response);
      return { status: payload ?? null, success: true };
    } catch (error) {
      return { ...this.handleApiError(error), status: null };
    }
  }

  /** Lista las identidades verificadas en SES (dominios y correos). */
  async getNotificationIdentities(): Promise<NotificationIdentitiesResponse> {
    try {
      const response = await adminApiService.get<any>(`${this.baseEndpoint}/notifications/identities`);
      const payload = this.unwrap(response);
      return { identities: payload?.identities ?? [], total: payload?.total ?? 0, success: true };
    } catch (error) {
      return { ...this.handleApiError(error), identities: [], total: 0 };
    }
  }

  /** Verifica una identidad nueva (correo o dominio) en SES. */
  async createNotificationIdentity(identity: string): Promise<NotificationIdentityActionResponse> {
    try {
      const response = await adminApiService.post<any>(
        `${this.baseEndpoint}/notifications/identities`, { identity });
      const payload = this.unwrap(response);
      return { result: payload ?? null, success: true };
    } catch (error) {
      return { ...this.handleApiError(error), result: null };
    }
  }

  /** Lee la configuración de notificaciones (remitente 'from'). Fase B. */
  async getNotificationConfig(): Promise<NotificationConfigResponse> {
    try {
      const response = await adminApiService.get<any>(`${this.baseEndpoint}/notifications/config`);
      const payload = this.unwrap(response);
      return { config: payload ?? null, success: true };
    } catch (error) {
      return { ...this.handleApiError(error), config: null };
    }
  }

  /** Edita el remitente 'from' que usan las Lambdas de envío. Fase B. */
  async updateNotificationConfig(fromAddress: string): Promise<NotificationConfigResponse> {
    try {
      const response = await adminApiService.patch<any>(
        `${this.baseEndpoint}/notifications/config`, { fromAddress });
      const payload = this.unwrap(response);
      return { config: payload ?? null, success: true };
    } catch (error) {
      return { ...this.handleApiError(error), config: null };
    }
  }

  /** Envía un correo de prueba con el remitente real para validar entregabilidad. */
  async sendTestNotification(to: string, subject?: string): Promise<NotificationTestResponse> {
    try {
      const response = await adminApiService.post<any>(
        `${this.baseEndpoint}/notifications/test`, { to, subject });
      const payload = this.unwrap(response);
      return { result: payload ?? null, success: true };
    } catch (error) {
      return { ...this.handleApiError(error), result: null };
    }
  }

  // -------------------------------------------------------------------------
  // Configuración global de visibilidad de la UI del cliente
  // -------------------------------------------------------------------------

  /** Lee la config de visibilidad (menú, marketplace, appDeploy, database, vm). */
  async getSettings(): Promise<SettingsResponse> {
    try {
      const response = await adminApiService.get<any>(`${this.baseEndpoint}/settings`);
      const payload = this.unwrap(response);
      return {
        settings: payload?.settings ?? null,
        version: payload?.version,
        updatedAt: payload?.updatedAt ?? null,
        updatedBy: payload?.updatedBy ?? null,
        source: payload?.source,
        success: true,
      };
    } catch (error) {
      return { ...this.handleApiError(error), settings: null };
    }
  }

  /**
   * Actualiza una o más secciones (deep-merge en el backend). Enviar
   * expectedVersion (el `version` leído) para detectar ediciones concurrentes.
   */
  async updateSettings(
    payload: UISettings & { expectedVersion?: number },
  ): Promise<SettingsResponse> {
    try {
      const response = await adminApiService.patch<any>(`${this.baseEndpoint}/settings`, payload);
      const data = this.unwrap(response);
      return {
        settings: data?.settings ?? null,
        version: data?.version,
        updatedAt: data?.updatedAt ?? null,
        updatedBy: data?.updatedBy ?? null,
        source: data?.source,
        success: true,
      };
    } catch (error) {
      return { ...this.handleApiError(error), settings: null };
    }
  }

  /**
   * Desenvuelve el wrapper { statusCode, message, data, timestamp } que añade el Lambda.
   */
  private unwrap(response: any): any {
    if (response && typeof response === 'object' && 'data' in response) {
      return response.data;
    }
    return response;
  }

  private normalizeClientItem(item: any): Client {
    return {
      tenantId: item.id || item.tenantId || '',
      tenantName: item.tenantName || item.email || item.id || '',
      email: item.email || '',
      status: ((item.status || 'active').toUpperCase()) as Client['status'],
      createdAt: item.created_at || item.createdAt || '',
      updatedAt: item.updated_at || item.updatedAt || '',
      totalCost: item.mrr ?? item.totalCost ?? 0,
      deuda: item.balance !== undefined ? Math.max(0, -Number(item.balance)) : (item.deuda ?? 0),
    };
  }

  private normalizeTenantItem(item: any): Client {
    return {
      tenantId: item.id || item.tenantId || '',
      tenantName: item.tenantName || item.name || item.email || item.id || '',
      email: item.email || '',
      status: ((item.status || 'active').toUpperCase()) as Client['status'],
      createdAt: item.created_at || item.createdAt || '',
      updatedAt: item.updated_at || item.updatedAt || '',
      totalCost: item.mrr ?? item.totalCost ?? 0,
      deuda: item.balance !== undefined ? Math.max(0, -Number(item.balance)) : (item.deuda ?? 0),
    };
  }

  private normalizeServiceItem(item: any): Service {
    return {
      serviceId: item.id || item.serviceId || '',
      tenantId: item.tenant_id || item.tenantId || '',
      name: item.name || '',
      type: item.type || '',
      status: ((item.status || 'stopped').toUpperCase()) as Service['status'],
      createdAt: item.created_at || item.createdAt || '',
      updatedAt: item.updated_at || item.updatedAt || '',
      cost: item.cost_per_hour !== undefined ? Number(item.cost_per_hour) : (item.cost ?? 0),
    };
  }

  private normalizeClientListResponse(response: any): ClientListResponse {
    const payload = this.unwrap(response);
    const items: any[] = Array.isArray(payload) ? payload : (payload?.clients ?? []);
    const clients = items.map((item) => this.normalizeClientItem(item));
    return { clients, total: payload?.total ?? clients.length };
  }

  private normalizePaymentListResponse(response: any): PaymentListResponse {
    const payload = this.unwrap(response);
    const items: any[] = Array.isArray(payload) ? payload : (payload?.payments ?? []);
    return { payments: items, total: payload?.total ?? items.length };
  }

  private normalizeAuditLogListResponse(response: any): AuditLogListResponse {
    const payload = this.unwrap(response);
    const items: any[] = Array.isArray(payload) ? payload : (payload?.logs ?? []);
    return { logs: items, total: payload?.total ?? items.length };
  }

  /**
   * Maneja errores de API de forma consistente
   */
  private handleApiError(error: any): any {
    const errorResponse = {
      success: false,
      error: error?.message || 'Error desconocido',
      errorCode: error?.status || 'UNKNOWN_ERROR',
    };

    // EspecÃ­fico para 401 (Unauthorized) - ya estÃ¡ manejado por apiService
    if (error?.status === 401) {
      return {
        ...errorResponse,
        error: 'API key invÃ¡lida o sesiÃ³n expirada',
        errorCode: 'UNAUTHORIZED',
      };
    }

    // EspecÃ­fico para 403 (Forbidden)
    if (error?.status === 403) {
      return {
        ...errorResponse,
        error: 'No tiene permisos para esta operaciÃ³n',
        errorCode: 'FORBIDDEN',
      };
    }

    // EspecÃ­fico para 404 (Not Found)
    if (error?.status === 404) {
      return {
        ...errorResponse,
        error: 'Recurso no encontrado',
        errorCode: 'NOT_FOUND',
      };
    }

    // Errores de validaciÃ³n 400
    if (error?.status === 400) {
      return {
        ...errorResponse,
        error: error?.errors || error?.message || 'Solicitud invÃ¡lida',
        errorCode: 'VALIDATION_ERROR',
      };
    }

    return errorResponse;
  }
}

/**
 * Producto del catálogo (tabla Products). PK = PRODUCT#<id>, SK = META.
 */
export interface Product {
  PK?: string;
  SK?: string;
  id: string;
  name: string;
  type: string;
  category: string;
  typeFamily: string;
  description?: string;
  price?: string | number;
  currency?: string;
  isActive: boolean;
  osType?: string;
  osFamily?: string;
  appType?: string;
  engine?: string;
  ami?: string;
  region?: string;
  sku?: string;
  stock?: number | string;
  /** Códigos de plan permitidos para este producto; vacío = todos los de su familia */
  allowedPlans?: string[];
  /** Override del mapeo producto→pricing (ver utils/pricingRef.ts) */
  pricingTypeFamily?: string;
  pricingOsType?: string;
  pricingOsFamily?: string;
  images?: string[];
  metadata?: Record<string, any> | string;
  createdAt?: string;
  updatedAt?: string;
}

export interface ProductListResponse {
  products: Product[];
  total: number;
  success: boolean;
  error?: string;
  errorCode?: string;
}

export interface ProductActionResponse {
  product: Product | null;
  success: boolean;
  error?: string;
  errorCode?: string;
}

/**
 * Item del catálogo de precios (KubiyPricingCatalog-prod): plan o add-on.
 */
export interface PricingItem {
  PK: string;
  SK: string;
  entityType: 'PRICE' | 'ADDON_PRICE';
  typeFamily: string;
  osType?: string;
  planCode?: string;
  planName?: string;
  displayName?: string;
  variantCode?: string;
  variantName?: string;
  addonCode?: string;
  addonName?: string;
  description?: string;
  currency: string;
  billingInterval: 'MONTH' | 'YEAR' | 'ONE_TIME';
  billingModel: 'FIXED' | 'PER_UNIT' | 'FIXED_WITH_OVERAGE' | 'TIERED';
  priceAmount: number;
  /** Precios multi-moneda en centavos, ej. { MXN: 49900, USD: 2900 } */
  prices?: Record<string, number>;
  status: 'ACTIVE' | 'INACTIVE';
  version?: number;
  priceId?: string;
  sortOrder?: number;
  includedResources?: Record<string, any>;
  overagePricing?: Record<string, any>;
  providerConfiguration?: Record<string, any>;
  compatiblePlans?: string[];
  configuration?: Record<string, any>;
  effectiveFrom?: string;
  createdAt?: string;
  updatedAt?: string;
}

/**
 * Payload de actualización de un item de precios. PK + SK identifican el item;
 * el resto son los campos editables permitidos por el backend.
 */
export interface PricingItemUpdate {
  PK: string;
  SK: string;
  expectedVersion?: number;
  planName?: string;
  displayName?: string;
  variantName?: string;
  description?: string;
  priceAmount?: number;
  prices?: Record<string, number>;
  currency?: string;
  billingInterval?: string;
  billingModel?: string;
  status?: string;
  includedResources?: Record<string, any>;
  overagePricing?: Record<string, any>;
  sortOrder?: number;
  addonName?: string;
  compatiblePlans?: string[];
  configuration?: Record<string, any>;
  effectiveFrom?: string;
}

export interface PricingConfig {
  /** Tipo de cambio de referencia USD→MXN que fija el admin (no es FX en vivo) */
  usdToMxn: number | null;
  updatedAt?: string;
}

export interface PricingListResponse {
  plans: PricingItem[];
  addons: PricingItem[];
  total: number;
  config?: PricingConfig;
  success: boolean;
  error?: string;
  errorCode?: string;
}

/** Payload para crear un plan nuevo desde cero (familias sin planes) */
export interface PricingPlanCreate {
  typeFamily: string;
  planCode: string;
  planName: string;
  /** centavos MXN — obligatorio, es lo que cobra la plataforma */
  priceAmount: number;
  variantCode?: string;
  variantName?: string;
  displayName?: string;
  description?: string;
  osType?: string;
  osFamily?: string;
  billingInterval?: string;
  billingModel?: string;
  prices?: Record<string, number>;
  includedResources?: Record<string, any>;
  status?: string;
}

/** Payload para crear un add-on nuevo */
export interface PricingAddonCreate {
  typeFamily: string;
  addonCode: string;
  addonName: string;
  /** centavos MXN */
  priceAmount: number;
  osType?: string;
  description?: string;
  billingInterval?: string;
  billingModel?: string;
  prices?: Record<string, number>;
  compatiblePlans?: string[];
  configuration?: Record<string, any>;
  status?: string;
}

/** Payload para crear una variante duplicando un plan existente */
export interface PricingVariantCreate {
  sourcePK: string;
  sourceSK: string;
  variantCode: string;
  variantName?: string;
  displayName?: string;
  priceAmount?: number;
  prices?: Record<string, number>;
  status?: string;
}

export interface PricingActionResponse {
  item: PricingItem | null;
  success: boolean;
  error?: string;
  errorCode?: string;
}

// ---------------------------------------------------------------------------
// Notificaciones / Email (SES)
// ---------------------------------------------------------------------------

export interface SesIdentity {
  name: string;
  type: 'EMAIL_ADDRESS' | 'DOMAIN' | string;
  verificationStatus: 'SUCCESS' | 'PENDING' | 'FAILED' | string;
  sendingEnabled: boolean | null;
}

export interface SesStatus {
  region: string;
  productionAccessEnabled: boolean;
  sendingEnabled: boolean;
  enforcementStatus?: string;
  reviewStatus?: string | null;
  quota: {
    max24HourSend?: number;
    maxSendRate?: number;
    sentLast24Hours?: number;
  };
  fromAddress: string;
  identities: SesIdentity[];
  identitiesVerified: number;
  metrics?: {
    windowDays: number;
    totals: Record<string, number>;
  };
}

export interface NotificationStatusResponse {
  status: SesStatus | null;
  success: boolean;
  error?: string;
  errorCode?: string;
}

export interface NotificationIdentitiesResponse {
  identities: SesIdentity[];
  total: number;
  success: boolean;
  error?: string;
  errorCode?: string;
}

export interface NotificationIdentityActionResponse {
  result: {
    identity: string;
    type: string;
    verificationStatus: string;
    dkimTokens?: string[];
    message?: string;
  } | null;
  success: boolean;
  error?: string;
  errorCode?: string;
}

export interface NotificationTestResponse {
  result: {
    to: string;
    messageId?: string;
    from: string;
  } | null;
  success: boolean;
  error?: string;
  errorCode?: string;
}

export interface NotificationConfig {
  fromAddress: string;
  fromEmail: string | null;
  defaultFrom: string;
  updatedAt?: string | null;
  source: string;
  warning?: string;
}

export interface NotificationConfigResponse {
  config: NotificationConfig | null;
  success: boolean;
  error?: string;
  errorCode?: string;
}

/**
 * Instancia singleton del servicio administrativo
 */
export const adminService = new AdminService();

